import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from 'react';
import { MdSkipPrevious, MdSkipNext } from 'react-icons/md';
import NoteService from '../../services/NoteService';
import './App.css'; // Assuming App.css contains styles for DrivingNotes
import NoteItem from "./NoteItem";
import todosData from "./TodosDataMVP";
import todosData2 from "./TodosDataMVPAut";
import { Context } from "../../index"; // Adjust path if needed
import { toast } from "react-toastify";
import { v4 } from "uuid";
import DeleteConfirmationModal from './DeleteConfirmation'; // Adjust path if needed
import Spinner from '../../UI/Spinner/Spinner'; // Adjust path if needed
import MyStats from "./MyStats/Mystats"; // Ensure this path is correct
import useIsMobile from "../../utils/useIsMobile"; // Adjust path if needed

function DrivingNotes() {
    const [drivingNotes, setDrivingNotes] = useState([]);
    const [statusValues, setStatusValues] = useState(['🟣Börjat', '🟠Visad kunskap', '🟢Godkänd']);
    const [currentNoteIndex, setCurrentNoteIndex] = useState(0); // Index in the flat drivingNotes array
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingGroupId, setDeletingGroupId] = useState(null);
    const [countdown, setCountdown] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    const { store } = useContext(Context);
    const [loading, setLoading] = useState(false);
    const isMobile = useIsMobile();
    const deleteInFlightRef = useRef(false);

    const headers = useMemo(() => ({
        'studentId': sessionStorage.getItem('studentId'),
    }), []);

    useEffect(() => {
        setLoading(true);
        NoteService.getAllDrivingNotes({ headers })
            .then(response => {

                const sortedDrivingNotes = response.data.drivingNotes.sort((a, b) => {
                    if (a.groupId !== b.groupId) {
                        return a.groupId.localeCompare(b.groupId);
                    }
                    return a.number - b.number; // Sort by lesson number within the group
                });
                setDrivingNotes(sortedDrivingNotes);
            })
            .catch(error => {
                console.error('Error fetching data:', error);
                toast.error('Kunde inte ladda övningskort.');
            })
            .finally(() => {
                setLoading(false);
            });
    }, [headers]);

    const totalOverallNotes = drivingNotes.length; // Renamed for clarity
    const currentNote = drivingNotes[currentNoteIndex];

    const uniqueGroupIds = useMemo(() => {
        if (!drivingNotes || drivingNotes.length === 0) return [];
        const ids = Array.from(new Set(drivingNotes.map((note) => note.groupId))).sort();
        return ids.reverse();
    }, [drivingNotes]);

    const totalGroups = uniqueGroupIds.length;

    const currentGroupNumber = useMemo(() => {
        if (!currentNote || totalGroups === 0) return 0;
        const groupIndex = uniqueGroupIds.indexOf(currentNote.groupId);
        if (groupIndex === -1) return 0;
        return totalGroups - groupIndex;
    }, [currentNote, uniqueGroupIds, totalGroups]);

    const notesInCurrentGroup = useMemo(() => {
        if (!currentNote || !drivingNotes.length) {
            return [];
        }
        return drivingNotes.filter(note => note.groupId === currentNote.groupId);
    }, [currentNote, drivingNotes]);

    const totalNotesInGroup = notesInCurrentGroup.length;

    const currentLessonNumberInGroup = useMemo(() => {
        if (!currentNote || !notesInCurrentGroup.length) {
            return 0;
        }
        const indexInGroup = notesInCurrentGroup.findIndex(note => note.id === currentNote.id);
        return indexInGroup + 1;
    }, [currentNote, notesInCurrentGroup]);

    const goToNextNote = () => {
        if (currentNoteIndex < totalOverallNotes - 1) {
            setCurrentNoteIndex(currentNoteIndex + 1);
        }
    };

    const goToPreviousNote = () => {
        if (currentNoteIndex > 0) {
            setCurrentNoteIndex(currentNoteIndex - 1);
        }
    };

    const updateDrivingNote = useCallback(async (updatedNoteData) => {
        try {
            await NoteService.updateDrivingNoteById(
                updatedNoteData.id,
                updatedNoteData.nytitle,
                updatedNoteData.statA, updatedNoteData.statB, updatedNoteData.statC,
                updatedNoteData.statD, updatedNoteData.statE, updatedNoteData.statF,
                updatedNoteData.statG, updatedNoteData.statH, updatedNoteData.statI,
                updatedNoteData.isDone,
                { headers }
            );
        } catch (error) {
            console.error('Error updating note on backend:', error);
            toast.error('Kunde inte spara ändringen.');
        }
    }, [headers]);

    const handleStatusChange = useCallback((e, drivingNoteId, fieldName) => {
        const newStatus = e.target.value;
        setDrivingNotes(prevDrivingNotes => {
            const updatedNotes = prevDrivingNotes.map(note => {
                if (note.id === drivingNoteId) {
                    const updatedNote = { ...note, [fieldName]: newStatus };
                    updateDrivingNote(updatedNote);
                    return updatedNote;
                }
                return note;
            });
            return updatedNotes;
        });
    }, [updateDrivingNote]);

    const handleClick = useCallback((drivingNoteId) => {
        setDrivingNotes(prevDrivingNotes => {
            const updatedNotes = prevDrivingNotes.map(note => {
                if (note.id === drivingNoteId) {
                    const newState = !note.isDone;
                    const updatedNote = { ...note, isDone: newState };
                    updateDrivingNote(updatedNote);
                    return updatedNote;
                }
                return note;
            });
            return updatedNotes;
        });
    }, [updateDrivingNote]);

    const createNewNotes = async () => {
        setLoading(true);
        try {
            const newNotes = [];
            const newGroupId = v4();
            const todosSource = store.user?.autotransmission ? todosData2.items : todosData.items;

            if (!todosSource || todosSource.length === 0) {
                console.warn("No data found for creation.");
                toast.error("Kunde inte skapa övningskort: Ingen data att utgå ifrån.");
                setLoading(false);
                return;
            }

            for (const item of todosSource) {
                const newNote = {
                    groupId: newGroupId, id: v4(), studentId: store.user?.id,
                    isDone: item.isDone || false, number: item.number, nytitle: item.nytitle,
                    catA: item.catA || '', statA: '', catB: item.catB || '', statB: '',
                    catC: item.catC || '', statC: '', catD: item.catD || '', statD: '',
                    catE: item.catE || '', statE: '', catF: item.catF || '', statF: '',
                    catG: item.catG || '', statG: '', catH: item.catH || '', statH: '',
                    catI: item.catI || '', statI: '',
                };
                newNotes.push(newNote);
            }

            await Promise.all(newNotes.map(async (note) => await NoteService.createDrivingNote(note, { headers })));
            const updatedResponse = await NoteService.getAllDrivingNotes({ headers });
            const sortedDrivingNotes = updatedResponse.data.drivingNotes.sort((a, b) => {
                if (a.groupId !== b.groupId) {
                    return a.groupId.localeCompare(b.groupId);
                }
                return a.number - b.number;
            });
            setDrivingNotes(sortedDrivingNotes);

            const firstNoteOfNewGroupIndex = sortedDrivingNotes.findIndex(note => note.groupId === newGroupId);

            if (firstNoteOfNewGroupIndex !== -1) {
                setCurrentNoteIndex(firstNoteOfNewGroupIndex);
            } else {
                console.warn("Could not find the first note of the newly created group. Setting index to 0.");
                setCurrentNoteIndex(0);
            }
            toast.success(`Ny lista skapad!`);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                toast.error( 'Max antal övningskort är uppnådd!');
            } else {
                toast.error('Ett oväntat fel inträffade, vänligen försök igen.');
            }
        } finally {
            setLoading(false);
        }
    };

    const deleteGroup = (groupId) => {
        if (groupId) {
            setDeletingGroupId(groupId);
            setShowDeleteModal(true);
        } else {
            toast.error("Kunde inte radera kortet: Ogiltigt ");
        }
    };

    const startCountdown = () => {
        setIsDeleting(true);
        setCountdown(3);
    };

    const handleDeleteConfirmed = useCallback(async () => {
        if (!deletingGroupId || deleteInFlightRef.current) return;
        deleteInFlightRef.current = true;
        setLoading(true);
        try {
            await NoteService.deleteDrivingNoteById(deletingGroupId, { headers });
            setDrivingNotes(prevDrivingNotes => {
                const remainingNotes = prevDrivingNotes.filter(n => n.groupId !== deletingGroupId);
                const newTotalNotes = remainingNotes.length;
                let newIndex = currentNoteIndex;

                if (newIndex >= newTotalNotes && newTotalNotes > 0) {
                    newIndex = newTotalNotes - 1;
                } else if (newTotalNotes === 0) {
                    newIndex = 0;
                }

                if (newIndex !== currentNoteIndex || newTotalNotes === 0) {
                    setCurrentNoteIndex(newIndex);
                } else if (newTotalNotes > 0 && !remainingNotes[newIndex]) {
                    setCurrentNoteIndex(0);
                }
                return remainingNotes;
            });
            toast.info(`Kortet är raderad!`);
        } catch (error) {
            toast.error('Kunde inte radera kortet.');
        } finally {
            deleteInFlightRef.current = false;
            setIsDeleting(false);
            setShowDeleteModal(false);
            setDeletingGroupId(null);
            setLoading(false);
        }
    }, [deletingGroupId, currentNoteIndex, headers]);

    const handleDeleteConfirmedRef = useRef(handleDeleteConfirmed);
    handleDeleteConfirmedRef.current = handleDeleteConfirmed;

    useEffect(() => {
        if (!isDeleting) return;
        const timer = setInterval(() => {
            setCountdown((prev) => {
                if (prev === 1) {
                    clearInterval(timer);
                    handleDeleteConfirmedRef.current();
                    return null;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [isDeleting]);

    const handleDeleteCanceled = () => {
        setIsDeleting(false);
        setShowDeleteModal(false);
        setDeletingGroupId(null);
        setCountdown(null);
    };

    const hasAnyCats = useMemo(() => {
        return (note) => {
            if (!note) return false;
            const cats = ['catA', 'catB', 'catC', 'catD', 'catE', 'catF', 'catG', 'catH', 'catI'];
            return cats.some(cat => note[cat]);
        };
    }, []);

    const hasVisibleNotesAcrossAllGroups = useMemo(() => {
        return drivingNotes.some(hasAnyCats);
    }, [drivingNotes, hasAnyCats]);

    useEffect(() => {
        if (totalOverallNotes > 0 && currentNoteIndex >= totalOverallNotes) {
            setCurrentNoteIndex(totalOverallNotes - 1);
        } else if (totalOverallNotes === 0 && currentNoteIndex !== 0) {
            setCurrentNoteIndex(0);
        }
    }, [totalOverallNotes, currentNoteIndex]);

    return (
        <div className="mt-64 center">
            <MyStats
                drivingNotes={drivingNotes}
                onSelectNoteIndex={setCurrentNoteIndex}
            />

            {!hasVisibleNotesAcrossAllGroups && !loading && drivingNotes.length === 0 && (
                <div>
                    <h3>Skapa övningskort genom att trycka på knappen nedan</h3>
                    <div className="arrow-container center">
                        <div className="arrow-down"></div>
                    </div>
                </div>
            )}

            <button className="mt-32 ghostbutton" style={{ maxWidth: 190 }} onClick={createNewNotes} disabled={loading}>
                Skapa nytt kort
            </button>

            <div className="jcc center">
                {loading ?
                    <Spinner />
                    :
                    <div className="center m-8 p-8" >
                        {totalOverallNotes > 0 && currentNote ? (
                            <div key={currentNote.groupId} className="center card mt-32" style={{ paddingTop: "30px", backgroundColor: "#e9e9e9" }}>
                                <span> Övningskort nummer</span>
                                <p style={{ fontSize: "5rem", color: "#bebdbd" }}>{currentGroupNumber}</p>
                                <button
                                    className="ghostbutton"
                                    style={{ maxWidth: 135 }}
                                    onClick={() => deleteGroup(currentNote.groupId)}
                                    disabled={isDeleting || loading || !currentNote?.groupId}
                                >
                                    Ta bort
                                </button>

                                <div className="center item card mt-32" style={{ paddingTop: "30px", backgroundColor: "rgba(255,255,255,0.62)" }}>
                                    <h1 className='center' style={{ color: "#bebdbd", width: "100%", textAlign: "center" }}> GRUNDLÄGGANDE MOMENT </h1>
                                    <div className={'flex cols font'} style={{fontSize:" 1.2rem"}}>
                                        <span style={{ margin: '0 15px' }}>
                                            Lektion {currentLessonNumberInGroup} av {totalNotesInGroup}
                                        </span>
                                        <span className={'p-24'}>  {currentNote.nytitle}</span>
                                    </div>
                                    <div className="pb-16">
                                        <button
                                            className= {isMobile? "ghostbuttonXS" : "ghostbuttonXL" }
                                            onClick={goToPreviousNote}
                                            disabled={currentNoteIndex === 0 || loading || isDeleting} // Navigation still uses overall index
                                        >
                                            <MdSkipPrevious /> {isMobile? "Förra" : "Föregående lektion"}
                                        </button>
                                        <button
                                            className= {isMobile? "ghostbuttonXS" : "ghostbuttonXL" }
                                            onClick={goToNextNote}
                                            disabled={currentNoteIndex === totalOverallNotes - 1 || loading || isDeleting} // Navigation still uses overall index
                                        >
                                            <MdSkipNext /> {isMobile? "Nästa" : "Nästa lektion" }
                                        </button>
                                    </div>
                                </div>

                                <div key={currentNote.id + "-container"} className="center container m-8" >
                                    <NoteItem
                                        key={currentNote.id}
                                        note={currentNote}
                                        number={currentNote.number} // Pass number if NoteItem uses it
                                        groupId={currentNote.groupId}
                                        handleClick={handleClick}
                                        handleStatusChange={handleStatusChange}
                                        statusValues={statusValues}
                                    />
                                </div>
                            </div>
                        ) : (
                            !loading && drivingNotes.length > 0 && <p>Valt kort kunde inte hittas.</p>
                        )}

                        <DeleteConfirmationModal
                            isOpen={showDeleteModal}
                            countdown={countdown}
                            isDeleting={isDeleting}
                            onCancel={handleDeleteCanceled}
                            onConfirm={startCountdown}
                        />
                    </div>
                }
            </div>
        </div>
    );
}

export default DrivingNotes;